import { useInfiniteQuery } from "@tanstack/react-query";
import { deviceService } from "../api/services/deviceService";
import type {
  DeviceListResult,
  DeviceListFilters,
} from "../schemas/deviceSchema";
import { DEFAULT_PAGE_SIZE } from "../constants";

export type DevicesInfiniteFilters = Omit<
  DeviceListFilters,
  "page" | "pageSize"
> & {
  pageSize?: number;
};

export const useDevicesInfinite = (filters: DevicesInfiniteFilters = {}) => {
  const { pageSize = DEFAULT_PAGE_SIZE, ...rest } = filters;

  const query = useInfiniteQuery<DeviceListResult, Error>({
    queryKey: ["devices", "infinite", { ...rest, pageSize }],
    queryFn: ({ pageParam }) =>
      deviceService.getDevices({
        ...rest,
        page: pageParam as number,
        pageSize,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasNextPage ? (lastPage.page ?? 0) + 1 : undefined,
    // Device rows change without the portal doing anything — a TV pairs itself
    // to a screen, a box drops offline, lastSeenAt ticks — and there is no
    // portal-facing device subscription to push those events. So poll on the
    // same 60s cadence the screens list uses as its safety net.
    refetchInterval: 60_000,
    // App.tsx sets a global 5-minute staleTime. Because Screen Management
    // unmounts this tab when you switch to Screens, that default meant coming
    // back within 5 minutes re-rendered the cached pages with no network call —
    // the list looked frozen until a full browser reload. Treat device data as
    // always stale so a tab switch or a window refocus re-fetches.
    staleTime: 0,
  });

  const devices = query.data?.pages.flatMap((p) => p.devices) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;

  return {
    devices,
    total,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};
